import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/providers/AuthProvider';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import type { AgreementPayment } from './types';

export function useAgreementPayments() {
  const { clientId } = useCurrentClient();
  return useQuery({
    queryKey: ['agreement_payments', clientId],
    enabled: !!clientId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<AgreementPayment[]> => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from('agreement_payments')
        .select('*')
        .eq('client_id', clientId)
        .order('payment_date', { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as AgreementPayment[];
    },
  });
}

export interface CreatePaymentInput {
  agreementId: string;
  referenceMonth: string; // YYYY-MM
  paymentDate: string; // YYYY-MM-DD
  grossAmount: number;
  glosaAmount: number;
  netAmount: number;
  accountId?: string | null;
  categoryId?: string | null;
  costCenterId?: string | null;
  notes?: string;
  file?: File;
  registerAsTransaction?: boolean;
}

export function useCreateAgreementPayment() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { clientId } = useCurrentClient();

  return useMutation({
    mutationFn: async (input: CreatePaymentInput) => {
      if (!clientId) throw new Error('Cliente não selecionado');

      // 1) Cria transação de receita se solicitado
      let transactionId: string | null = null;
      if (input.registerAsTransaction && input.accountId && input.netAmount > 0) {
        const { data: tx, error: txErr } = await supabase
          .from('transactions')
          .insert({
            client_id: clientId,
            type: 'income',
            description: `Repasse convênio — ${input.referenceMonth}`,
            amount: input.netAmount,
            date: input.paymentDate,
            payment_date: input.paymentDate,
            status: 'completed',
            account_id: input.accountId,
            category_id: input.categoryId ?? null,
            cost_center_id: input.costCenterId ?? null,
            notes: input.notes?.trim() || null,
          })
          .select()
          .single();
        if (txErr) throw txErr;
        transactionId = (tx as { id: string }).id;
      }

      // 2) Insere o repasse (sem file_path por enquanto)
      const { data: pay, error: payErr } = await supabase
        .from('agreement_payments')
        .insert({
          client_id: clientId,
          agreement_id: input.agreementId,
          reference_month: input.referenceMonth,
          payment_date: input.paymentDate,
          gross_amount: input.grossAmount,
          glosa_amount: input.glosaAmount,
          net_amount: input.netAmount,
          account_id: input.accountId ?? null,
          transaction_id: transactionId,
          notes: input.notes?.trim() || null,
          uploaded_by_user_id: user?.id ?? null,
        })
        .select()
        .single();
      if (payErr) {
        if (transactionId) {
          await supabase
            .from('transactions')
            .update({ deleted_at: new Date().toISOString() })
            .eq('id', transactionId);
        }
        throw payErr;
      }
      const created = pay as AgreementPayment;

      // 3) Upload do arquivo (se houver)
      if (input.file) {
        const sanitized = input.file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${clientId}/${created.id}_${sanitized}`;
        const { error: uploadErr } = await supabase.storage
          .from('agreement-payments')
          .upload(path, input.file, {
            contentType: input.file.type,
            upsert: false,
          });
        if (uploadErr) {
          throw new Error(`Repasse criado mas anexo falhou: ${uploadErr.message}`);
        }
        const { error: updErr } = await supabase
          .from('agreement_payments')
          .update({
            file_path: path,
            mime_type: input.file.type,
            size_bytes: input.file.size,
          })
          .eq('id', created.id);
        if (updErr) throw updErr;
      }

      return created;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agreement_payments'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['timeline'] });
      toast.success('Repasse registrado.');
    },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
}

export function useDeleteAgreementPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payment: AgreementPayment) => {
      const nowIso = new Date().toISOString();
      const { error } = await supabase
        .from('agreement_payments')
        .update({ deleted_at: nowIso })
        .eq('id', payment.id);
      if (error) throw error;
      if (payment.transaction_id) {
        await supabase
          .from('transactions')
          .update({ deleted_at: nowIso })
          .eq('id', payment.transaction_id);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agreement_payments'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      toast.success('Repasse excluído.');
    },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
}

export async function getAgreementPaymentSignedUrl(filePath: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from('agreement-payments')
    .createSignedUrl(filePath, 60 * 60);
  if (error) return null;
  return data?.signedUrl ?? null;
}
